import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import {
  isTikTokPrivacyLevel,
  isTikTokPrivacyLevelDisabled,
  TikTokPrivacyLevel,
  TikTokSettingsDraft,
} from 'fym-shared/src/music24/tiktokPostSettings';
import { TypedMemo } from '../../../../common/types';
import { tiktokMessages } from './tiktokMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the settings sections; each part uses its own slice of it */
import styles from '../settings/PlatformSettings.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TikTokPrivacyFieldProps {
  draft: TikTokSettingsDraft;
  /** The levels that `creator_info` offers, widest first. */
  options: TikTokPrivacyLevel[];
  onChange: (level: TikTokPrivacyLevel) => void;
}

const LEVEL_MESSAGES = {
  PUBLIC_TO_EVERYONE: tiktokMessages.privacyPublic,
  MUTUAL_FOLLOW_FRIENDS: tiktokMessages.privacyFriends,
  FOLLOWER_OF_CREATOR: tiktokMessages.privacyFollowers,
  SELF_ONLY: tiktokMessages.privacyOnlyMe,
} as const;

/**
 * "Who can see this post" (TikTok Direct Post G3). No level is picked at the start. The list is the
 * `privacy_level_options` of `creator_info`. Branded content cannot be "Only me".
 */
const TikTokPrivacyFieldComponent: React.FC<TikTokPrivacyFieldProps> = ({ draft, options, onChange }) => {
  const intl = useIntl();
  const label = intl.formatMessage(tiktokMessages.privacyLabel);

  const change = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (isTikTokPrivacyLevel(value)) {
        onChange(value);
      }
    },
    [onChange]
  );

  const items = useMemo(
    () =>
      options.map((level) => {
        const disabled = isTikTokPrivacyLevelDisabled(level, draft);
        const reason = disabled ? tiktokMessages.privacyBrandedPrivate : null;
        return { level, disabled, reason };
      }),
    [draft, options]
  );

  return (
    <div className={styles.field}>
      <label className={styles.eyebrow} htmlFor="tiktok-privacy">
        {label}
      </label>
      <Input
        id="tiktok-privacy"
        type="select"
        value={draft.privacyLevel ?? ''}
        invalid={draft.privacyLevel === null}
        onChange={change}
      >
        <option value="" disabled>
          {intl.formatMessage(tiktokMessages.privacyPlaceholder)}
        </option>
        {items.map(({ level, disabled, reason }) => (
          <option key={level} value={level} disabled={disabled}>
            {intl.formatMessage(LEVEL_MESSAGES[level])}
            {reason === null ? '' : ` - ${intl.formatMessage(reason)}`}
          </option>
        ))}
      </Input>
    </div>
  );
};

export const TikTokPrivacyField = TypedMemo(TikTokPrivacyFieldComponent);
